const APP_DOMAIN = "nazare.app";
const AUTH_SUBDOMAIN = "auth";
const DEV_APP_DOMAIN = `dev.${APP_DOMAIN}`;

function normaliseOrigin(value: string): string {
  return new URL(value).origin;
}

function isLocalHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

function isNazareHostname(hostname: string): boolean {
  return hostname === APP_DOMAIN || hostname.endsWith(`.${APP_DOMAIN}`);
}

function deploymentAppDomain(hostname: string): string {
  if (!isNazareHostname(hostname)) {
    throw new Error(`Unsupported Nazare app hostname: "${hostname}".`);
  }
  return hostname === DEV_APP_DOMAIN || hostname.endsWith(`.${DEV_APP_DOMAIN}`) ? DEV_APP_DOMAIN : APP_DOMAIN;
}

function assertAuthHostname(hostname: string): void {
  if (hostname !== `${AUTH_SUBDOMAIN}.${APP_DOMAIN}` && hostname !== `${AUTH_SUBDOMAIN}.${DEV_APP_DOMAIN}`) {
    throw new Error(`Expected an auth.nazare.app hostname, received "${hostname}".`);
  }
}

export function deriveMainAppUrl(authAppUrl: string, override?: string): string {
  if (override) return normaliseOrigin(override);

  const url = new URL(authAppUrl);
  assertAuthHostname(url.hostname);
  url.hostname = url.hostname.slice(AUTH_SUBDOMAIN.length + 1);
  url.pathname = "";
  url.search = "";
  url.hash = "";
  return url.origin;
}

export function deriveApiDomain(authAppUrl: string): string {
  const url = new URL(authAppUrl);
  assertAuthHostname(url.hostname);
  url.hostname = url.hostname === `${AUTH_SUBDOMAIN}.${DEV_APP_DOMAIN}` ? `api.${DEV_APP_DOMAIN}` : `api.${APP_DOMAIN}`;
  url.pathname = "";
  url.search = "";
  url.hash = "";
  return url.origin;
}

export function resolveApiDomain(authAppUrl: string, override?: string): string {
  return override ? normaliseOrigin(override) : deriveApiDomain(authAppUrl);
}

/** Sign-out is a query param, never a path: this app is served straight from S3 with no
 * SPA fallback, so /logout is an AccessDenied rather than the app. */
export function isLogoutRequest(authAppUrl: string): boolean {
  return new URL(authAppUrl).searchParams.has("logout");
}

export function getAuthReturnUrl(authAppUrl: string): URL | null {
  const authUrl = new URL(authAppUrl);
  const returnTo = authUrl.searchParams.get("return_to");
  if (!returnTo) return null;

  const returnUrl = new URL(returnTo);
  if (isLocalHostname(authUrl.hostname)) {
    if (!isLocalHostname(returnUrl.hostname)) {
      throw new Error("The auth return URL must target a local app origin.");
    }
    return returnUrl;
  }

  assertAuthHostname(authUrl.hostname);
  if (
    returnUrl.protocol !== authUrl.protocol ||
    deploymentAppDomain(returnUrl.hostname) !== deploymentAppDomain(authUrl.hostname)
  ) {
    throw new Error("The auth return URL must target an app in the matching Nazare deployment.");
  }

  return returnUrl;
}
